import React, { useCallback, useEffect, useLayoutEffect, useMemo, useContext, useState, forwardRef, useRef } from 'react';
import {
    useFloating, autoUpdate, flip, shift, useMergeRefs, hide, arrow, useDismiss,
} from '@floating-ui/react';
import { FreeFocusInside } from 'react-focus-lock';
import { isEventTargetInsideClickable, UuiContext } from '@epam/uui-core';
import type { LayoutLayer, DropdownProps } from '@epam/uui-core';
import { getFallbackPlacements } from '../helpers';
import { Portal } from './Portal';
import { isInteractedOutsideDropdown } from './DropdownHelpers';

function DropdownComponent(props: DropdownProps, ref: React.ForwardedRef<HTMLElement>) {
    const {
        value: controlledOpen,
        onValueChange: setControlledOpen,
        isNotUnfoldable,
        openOnHover,
        openOnFocus,
        openOnClick,
        closeOnMouseLeave,
        closeOnClickOutside,
        closeOnTargetClick,
        openDelay,
        closeDelay,
        onClose,
        virtualTarget,
        renderTarget,
        renderBody,
        closeBodyOnTogglerHidden,
        zIndex,
        portalTarget,
        placement = 'bottom-start',
        fallbackPlacements,
        middleware,
        boundaryElement,
        closeOnEscape = true,
        fixedBodyPosition = false,
    } = props;

    const uuiContext = useContext(UuiContext);

    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const initialBodyPosition = useRef<{ x: number, y: number } | null>(null);

    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = setControlledOpen ?? setUncontrolledOpen;

    const targetNodeRef = useRef<HTMLElement | null>(null);
    const bodyNodeRef = useRef<HTMLElement | null>(null);
    const arrowRef = useRef<HTMLDivElement | null>(null);
    const lastOpenedMsRef = useRef<number>(0);
    const togglerWidthRef = useRef<number>(0);
    const togglerHeightRef = useRef<number>(0);
    const layerRef = useRef<LayoutLayer | null>(null);
    const openDropdownTimerIdRef = useRef<NodeJS.Timeout | null>(null);
    const closeDropdownTimerIdRef = useRef<NodeJS.Timeout | null>(null);
    const isTargetHoveredRef = useRef<boolean | null>(null);

    const isOpened = useCallback(() => open, [open]);

    const handleOpenedChange = useCallback((newOpened: boolean) => {
        setOpen(newOpened);

        if (newOpened) {
            lastOpenedMsRef.current = new Date().getTime();
        }
    }, [setOpen]);

    const defaultMiddleware = [
        flip({ fallbackPlacements: fallbackPlacements ?? getFallbackPlacements(placement) }),
        shift({ boundary: boundaryElement, rootBoundary: 'viewport' }),
        hide(),
    ];

    if (arrowRef.current) {
        defaultMiddleware.push(arrow({ element: arrowRef }));
    }

    const { x, y, refs, strategy, placement: finalPlacement, middlewareData, update, isPositioned, context } = useFloating({
        middleware: defaultMiddleware.concat(middleware || []),
        placement: placement,
        strategy: 'fixed',
        open,
        onOpenChange: handleOpenedChange,
        whileElementsMounted: autoUpdate,
    });

    const { floating } = useDismiss(
        context,
        {
            enabled: closeOnEscape,
            escapeKey: closeOnEscape,
            outsidePress: false,
            ancestorScroll: false,
            referencePress: false,
        },
    );

    // Force update when the virtualTarget changes.
    useEffect(() => {
        update();
    }, [virtualTarget, update]);

    const clearOpenDropdownTimer = () => {
        if (openDropdownTimerIdRef.current) {
            clearTimeout(openDropdownTimerIdRef.current);
            openDropdownTimerIdRef.current = null;
        }
    };

    const clearCloseDropdownTimer = () => {
        if (closeDropdownTimerIdRef.current) {
            clearTimeout(closeDropdownTimerIdRef.current);
            closeDropdownTimerIdRef.current = null;
        }
    };

    const setOpenDropdownTimer = () => {
        openDropdownTimerIdRef.current = setTimeout(() => {
            // Use requestAnimationFrame to batch state updates
            requestAnimationFrame(() => {
                handleOpenedChange(true);
                clearOpenDropdownTimer();
            });
        }, openDelay || 0);
    };

    const setCloseDropdownTimer = (delay: number) => {
        closeDropdownTimerIdRef.current = setTimeout(() => {
            // Use requestAnimationFrame to batch state updates
            requestAnimationFrame(() => {
                handleOpenedChange(false);
                clearCloseDropdownTimer();
            });
        }, delay);
    };

    const handleMouseEnter = useCallback(() => {
        isTargetHoveredRef.current = true;
        clearCloseDropdownTimer();
        if (openDelay) {
            setOpenDropdownTimer();
        } else {
            handleOpenedChange(true);
        }
    }, []);

    const handleMouseLeave = useCallback(() => {
        isTargetHoveredRef.current = false;
        clearOpenDropdownTimer();

        if (closeOnMouseLeave !== 'boundary') {
            // For boundary mode we have separate logic on onMouseMove handler
            if (closeDelay) {
                open && setCloseDropdownTimer(closeDelay);
            } else {
                handleOpenedChange(false);
            }
        }
    }, [open]);

    const handleFocus = useCallback(() => {
        clearCloseDropdownTimer();
        if (openDelay) {
            setOpenDropdownTimer();
        } else {
            handleOpenedChange(true);
        }
    }, []);

    const handleBlur = useCallback((e: FocusEvent | React.FocusEvent<HTMLElement>) => {
        if (isTargetHoveredRef.current) return;

        const relatedTarget = e.relatedTarget as Node | null;
        const isFocusWithinTarget = relatedTarget && (targetNodeRef.current?.contains(relatedTarget) || bodyNodeRef.current?.contains(relatedTarget));

        if (isFocusWithinTarget) {
            return;
        }

        clearOpenDropdownTimer();
        if (closeDelay) {
            setCloseDropdownTimer(closeDelay);
        } else {
            handleOpenedChange(false);
        }
    }, []);

    const isClientInArea = (e: MouseEvent) => {
        const areaPadding = 30;
        const rect = bodyNodeRef.current?.getBoundingClientRect();

        if (rect) {
            const { height, width } = rect;

            if (rect.y && rect.x && width && height) {
                return rect.x - areaPadding <= e.clientX && e.clientX <= rect.x + areaPadding + width
                    && rect.y - areaPadding <= e.clientY && e.clientY <= rect.y + height + areaPadding;
            }
        }

        return false;
    };

    const getIsInteractedOutside = (event: Event) => {
        return isInteractedOutsideDropdown(
            event,
            [
                bodyNodeRef.current,
                targetNodeRef.current,
            ],
        );
    };

    const isInteractedOutside = useCallback((e: Event) => {
        if (!isOpened()) return false;
        return getIsInteractedOutside(e);
    }, [isOpened()]);

    const handleMouseMove = (e: MouseEvent) => {
        if (!bodyNodeRef.current || !targetNodeRef.current) return;

        if (isInteractedOutside(e) && isClientInArea(e) && !closeDropdownTimerIdRef.current) {
            // User cursor in boundary area, but not inside toggler or body
            clearOpenDropdownTimer();
            setCloseDropdownTimer(closeDelay ?? 1500);
        } else if (isInteractedOutside(e) && !isClientInArea(e)) {
            // User leave boundary area, close dropdown immediately or with closeDelay
            if (closeDelay && !closeDropdownTimerIdRef.current) {
                isOpened() && setCloseDropdownTimer(closeDelay);
            } else if (!closeDelay) {
                clearCloseDropdownTimer();
                handleOpenedChange(false);
            }
        } else if (!isInteractedOutside(e) && closeDropdownTimerIdRef.current) {
            // User returned to the toggler or body area, we need to clear close timer
            clearCloseDropdownTimer();
        }
    };

    const handleTargetClick = (e: React.SyntheticEvent<HTMLElement>) => {
        if (!isNotUnfoldable && !(e && isEventTargetInsideClickable(e))) {
            const currentValue = isOpened();
            const newValue = closeOnTargetClick === false ? true : !currentValue;

            if (currentValue !== newValue) {
                handleOpenedChange(newValue);
            }
        }
    };

    const getTargetClickHandler = useCallback(() => {
        if (openOnClick || !openOnHover) {
            return handleTargetClick;
        }
        return undefined;
    }, [openOnClick, openOnHover, handleTargetClick]);

    /*
    * Ref ensures onClose always runs the latest callback (avoids stale closure when bodyProps.onClose
    * is called from a stale render). See https://github.com/epam/UUI/issues/3011
    */
    const onCloseHandlerRef = useRef<DropdownProps['onClose']>(null);

    const onCloseHandler = useCallback(() => {
        if (onClose) onClose();
        else handleOpenedChange(false);
    }, [onClose, handleOpenedChange]);

    onCloseHandlerRef.current = onCloseHandler;

    const clickOutsideHandler = useCallback((e: Event) => {
        if (isInteractedOutside(e)) {
            handleOpenedChange(false);
        }
    }, [isInteractedOutside, handleOpenedChange]);

    // We'll use this function to get the reference element (either virtual or real)
    const getReferenceElement = () => {
        return virtualTarget || targetNodeRef.current;
    };

    // Modify this function to use the right reference element
    const updateTogglerSize = () => {
        const reference = getReferenceElement();
        if (reference) {
            const rect = reference.getBoundingClientRect();
            togglerWidthRef.current = rect.width;
            togglerHeightRef.current = rect.height;

            // Force update when toggler size changes
            if (virtualTarget) {
                update();
            }
        }
    };

    // Only use the real DOM ref if we're not using virtual references
    const mergedRefs = useMergeRefs([refs.setReference, targetNodeRef, ref]);
    const mergedTargetRef = virtualTarget
        ? (node: HTMLElement | null) => { targetNodeRef.current = node; }
        : mergedRefs;

    // Render target only if not using virtual reference
    const targetElement = !virtualTarget ? renderTarget({
        onClick: getTargetClickHandler(),
        isOpen: isOpened(),
        isDropdown: true,
        ref: mergedTargetRef,
        toggleDropdownOpening: handleOpenedChange,
        isInteractedOutside: getIsInteractedOutside,
        onFocus: openOnFocus ? handleFocus : undefined,
        onBlur: openOnFocus ? handleBlur : undefined,
    }) : null;

    // Set the reference element based on virtual or real element
    useEffect(() => {
        if (virtualTarget && refs.setPositionReference) {
            refs.setPositionReference(virtualTarget);
            // Force an initial update
            requestAnimationFrame(() => {
                update();
            });
        }
    }, [virtualTarget, refs.setPositionReference, update]);

    const body = useMemo(() => renderBody({
        onClose: () => onCloseHandlerRef.current(),
        togglerWidth: togglerWidthRef.current,
        togglerHeight: togglerHeightRef.current,
        scheduleUpdate: update,
        isOpen: isOpened(),
        arrowProps: {
            ref: arrowRef,
            style: middlewareData.arrow ? {
                top: middlewareData.arrow.y,
                left: middlewareData.arrow.x,
            } : {},
        },
        placement: finalPlacement,
    }), [
        renderBody,
        onCloseHandlerRef.current,
        togglerWidthRef.current,
        togglerHeightRef.current,
        update,
        isOpened,
        middlewareData?.arrow?.y,
        middlewareData?.arrow?.x,
        finalPlacement,
    ]);

    const mergedBodyRef = useMergeRefs([refs.setFloating, bodyNodeRef]);

    useEffect(() => {
        layerRef.current = uuiContext.uuiLayout?.getLayer();

        return () => {
            layerRef.current && uuiContext.uuiLayout?.releaseLayer(layerRef.current);
        };
    }, []);

    useEffect(() => {
        if (isOpened()) {
            window.addEventListener('dragstart', clickOutsideHandler);

            if (closeOnClickOutside !== false) {
                window.addEventListener('click', clickOutsideHandler, true);
            }
        }

        return () => {
            window.removeEventListener('dragstart', clickOutsideHandler);
            window.removeEventListener('click', clickOutsideHandler, true);
        };
    }, [closeOnClickOutside, clickOutsideHandler, isOpened()]);

    useEffect(() => {
        if (open && closeOnMouseLeave === 'boundary') {
            window.addEventListener('mousemove', handleMouseMove);
        } else if (closeOnMouseLeave === 'boundary') {
            window.removeEventListener('mousemove', handleMouseMove);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, [open, closeOnMouseLeave]);

    useEffect(() => {
        if (openOnHover && !openOnClick) {
            targetNodeRef.current?.addEventListener?.('mouseenter', handleMouseEnter);

            if (closeOnMouseLeave !== false) {
                targetNodeRef.current?.addEventListener?.('mouseleave', handleMouseLeave);
            }
        }

        return () => {
            targetNodeRef.current?.removeEventListener?.('mouseenter', handleMouseEnter);
            targetNodeRef.current?.removeEventListener?.('mouseleave', handleMouseLeave);
        };
    }, [
        openOnHover,
        openOnClick,
        closeOnMouseLeave,
        handleMouseEnter,
        handleMouseLeave,
    ]);

    useEffect(() => {
        if (openOnFocus) {
            targetNodeRef.current?.addEventListener?.('focusin', handleFocus);
            targetNodeRef.current?.addEventListener?.('focusout', handleBlur);
        }

        return () => {
            targetNodeRef.current?.removeEventListener?.('focusin', handleFocus);
            targetNodeRef.current?.removeEventListener?.('focusout', handleBlur);
        };
    }, [
        openOnFocus,
        handleFocus,
        handleBlur,
    ]);

    useLayoutEffect(() => {
        if (isPositioned && middlewareData.hide?.referenceHidden && (closeBodyOnTogglerHidden !== false)) {
            handleOpenedChange(false);
        }
    }, [middlewareData.hide?.referenceHidden, closeBodyOnTogglerHidden, isPositioned, handleOpenedChange]);

    useEffect(() => {
        const reference = virtualTarget || targetNodeRef.current;
        // Only set up autoUpdate when both refs are present and dropdown is open
        if (open && reference && bodyNodeRef.current) {
            const cleanup = autoUpdate(
                reference,
                bodyNodeRef.current,
                update,
                {
                    ancestorScroll: (!virtualTarget && !!targetNodeRef.current?.isConnected && !!bodyNodeRef.current.isConnected),
                    ancestorResize: (!virtualTarget && !!targetNodeRef.current?.isConnected && !!bodyNodeRef.current.isConnected),
                    elementResize: (!virtualTarget && !!targetNodeRef.current?.isConnected && !!bodyNodeRef.current.isConnected),
                },
            );
            return cleanup;
        }
    }, [open, update, virtualTarget]);

    const shouldShowBody = open && !isNotUnfoldable;

    if (shouldShowBody) {
        updateTogglerSize();
    }

    useEffect(() => {
        if (!open) {
            initialBodyPosition.current = null;
        }
    }, [open]);

    useEffect(() => {
        if (fixedBodyPosition) {
            if (open && initialBodyPosition.current == null && x && y) {
                initialBodyPosition.current = { x, y };
            }
        }
    }, [open, x, y, initialBodyPosition, fixedBodyPosition]);

    return (
        <>
            {targetElement}
            {shouldShowBody && (
                <Portal target={ portalTarget }>
                    <FreeFocusInside>
                        <div
                            role="dialog"
                            className="uui-popper"
                            aria-hidden={ !isOpened() }
                            ref={ mergedBodyRef }
                            onKeyDown={ floating?.onKeyDown }
                            style={ {
                                position: strategy,
                                top: (fixedBodyPosition && initialBodyPosition.current != null) ? initialBodyPosition.current.y : (y ?? 0),
                                left: (fixedBodyPosition && initialBodyPosition.current != null) ? initialBodyPosition.current.x : (x ?? 0),
                                zIndex: zIndex != null ? zIndex : layerRef.current?.zIndex,
                            } }
                            data-placement={ finalPlacement }
                        >
                            {body}
                        </div>
                    </FreeFocusInside>
                </Portal>
            )}
        </>
    );
}

export const Dropdown = forwardRef(DropdownComponent);
